/**
 * useSignupFlowDecision Hook
 *
 * React hook that computes current signup flow state and returns routing decision.
 * Uses the canonical resolver (signupFlowResolver.ts) as single source of truth.
 *
 * Contract: SIGNUP_VERIFICATION_ONBOARDING_CONTRACT_V1.md v2.2.0 §6
 */

import { useMemo, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { TeamContext } from '@/contexts/TeamContext';
import { isEffectivelyVerified } from '@/lib/auth/providerUtils';
import { resolveSignupFlowRoute, type SignupFlowRouteDecision } from './signupFlowResolver';

/**
 * Hook that computes signup flow state and returns routing decision
 *
 * CRITICAL: TeamProvider is OPTIONAL to support pre-verification routes.
 * Contract §5.3: R_VERIFY_NOTICE must NOT require TeamProvider.
 *
 * @returns Routing decision from canonical resolver
 */
export function useSignupFlowDecision(): SignupFlowRouteDecision {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();

  // CRITICAL: Make TeamProvider optional for pre-verification routes
  // Use useContext instead of useTeam hook to avoid "must be used within TeamProvider" error
  const teamContext = useContext(TeamContext);
  const { activeTeamId, team, onboardingJustCompleted } = teamContext || {
    activeTeamId: null,
    team: null,
    onboardingJustCompleted: false,
  };

  const decision = useMemo(() => {
    // Compute state inputs for resolver
    const signedIn = isAuthenticated && user != null;
    const verificationSatisfied = isEffectivelyVerified(user);

    // CRITICAL FIX: Handle eventual consistency for hasActiveTeam
    // Contract §6 Case 3: "Has team but not verified → verify email"
    //
    // After createTeamForOAuthUser() sets activeTeamId, the team doc may not be
    // loaded in TeamContext yet. For unverified password users, we must treat
    // activeTeamId presence as proof that team exists to satisfy Case 3.
    //
    // This prevents the bug where:
    // 1. CreateTeamPage navigates to /verify-email
    // 2. Resolver sees hasActiveTeam=false (team not loaded yet)
    // 3. Case 3 doesn't match, falls to fallback → /dashboard
    // 4. /verify-email redirects away → infinite loop
    //
    // Contract-conformant because:
    // - createTeamForOAuthUser sets activeTeamId atomically with team creation
    // - If activeTeamId exists, team doc WILL exist (eventual consistency)
    // - For unverified users, we MUST route to /verify-email per §6 Case 3
    const hasActiveTeam = verificationSatisfied
      ? Boolean(activeTeamId && team) // Verified users: require team doc loaded
      : Boolean(activeTeamId); // Unverified users: activeTeamId is sufficient proof

    // Check onboarding completion
    // Use optimistic flag OR Firestore field
    const onboardingCompleted = onboardingJustCompleted || Boolean(team?.onboardingCompletedAt);

    // Check if user has any team memberships
    // CRITICAL FIX: Handle eventual consistency after team creation
    // Contract §10.1: After team creation, membership may not be immediately visible
    // due to Cloud Function delay. Treat activeTeamId presence as proof of membership
    // to prevent routing loop back to create-team.
    //
    // This is contract-conformant because:
    // - createTeamForOAuthUser sets activeTeamId atomically with team creation
    // - If activeTeamId exists, membership WILL exist (eventual consistency)
    // - Prevents Case 2 (no memberships → create-team) from firing incorrectly
    const hasAnyTeamMembership = Boolean(activeTeamId) || hasActiveTeam;

    // Call canonical resolver
    return resolveSignupFlowRoute({
      signedIn,
      verificationSatisfied,
      hasActiveTeam,
      onboardingCompleted,
      hasAnyTeamMembership,
      activeTeamId,
      currentPath: location.pathname,
    });
  }, [isAuthenticated, user, activeTeamId, team, onboardingJustCompleted, location.pathname]);

  return decision;
}
